import { useCallback, useEffect, useMemo } from "react";

import { MultiSelect, Select, SimpleGrid, Stack, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";

import { Button, DatePicker, Modal, TextInput } from "components";
import { UserDataOptionsResponse, UserDataRequest, UserDataResponse, UserResponse } from "types";
import { AppUtils, DateUtils } from "utils";

export interface WorkName {
  firstName: string;
  lastName: string;
}

interface ProfileUserDataModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (data: UserDataRequest, workName?: WorkName) => Promise<void>;
  loading: boolean;
  options?: UserDataOptionsResponse;
  optionsLoading?: boolean;
  userData?: UserDataResponse | null;
  user?: UserResponse;
  canEditWorkName?: boolean;
}

interface ProfileUserDataFormValues {
  workFirstName: string;
  workLastName: string;
  realFirstName: string;
  realLastName: string;
  privatePhone: string;
  privateEmail: string;
  workStartDate: Date | null;
  birthDate: Date | null;
  location: string;
  contractType: string | null;
  contractEndDate: Date | null;
  contactPerson: string;
  languages: string[];
}

const EMPTY_FORM_VALUES: ProfileUserDataFormValues = {
  workFirstName: "",
  workLastName: "",
  realFirstName: "",
  realLastName: "",
  privatePhone: "",
  privateEmail: "",
  workStartDate: null,
  birthDate: null,
  location: "",
  contractType: null,
  contractEndDate: null,
  contactPerson: "",
  languages: [],
};

function parseDate(value?: string | null): Date | null {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function splitFullName(fullName?: string) {
  const normalized = fullName?.trim() ?? "";

  if (!normalized) {
    return { firstName: "", lastName: "" };
  }

  const parts = normalized.split(/\s+/);

  return {
    firstName: parts[0] ?? "",
    lastName: parts.slice(1).join(" "),
  };
}

function getWorkName(user?: UserResponse): WorkName {
  if (user?.firstName || user?.lastName) {
    return { firstName: user.firstName ?? "", lastName: user.lastName ?? "" };
  }

  return splitFullName(user?.fullName);
}

function mapUserDataToFormValues(
  userData?: UserDataResponse | null,
  user?: UserResponse,
): ProfileUserDataFormValues {
  const workName = getWorkName(user);

  if (!userData) {
    return {
      ...EMPTY_FORM_VALUES,
      workFirstName: workName.firstName,
      workLastName: workName.lastName,
      realFirstName: workName.firstName,
      realLastName: workName.lastName,
    };
  }

  return {
    workFirstName: workName.firstName,
    workLastName: workName.lastName,
    realFirstName: userData.realFirstName ?? "",
    realLastName: userData.realLastName ?? "",
    privatePhone: userData.privatePhone ?? "",
    privateEmail: userData.privateEmail ?? "",
    workStartDate: parseDate(userData.workStartDate),
    birthDate: parseDate(userData.birthDate),
    location: userData.location ?? "",
    contractType: userData.contractType ?? null,
    contractEndDate: parseDate(userData.contractEndDate),
    contactPerson: userData.contactPerson ?? "",
    languages: userData.languages ?? [],
  };
}

function trimToNullable(value: string) {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isPermanentContractType(contractType: string | null) {
  return !contractType || contractType === "PERMANENT";
}

function buildPayload(values: ProfileUserDataFormValues): UserDataRequest {
  const contractEndDate = isPermanentContractType(values.contractType)
    ? null
    : values.contractEndDate
      ? DateUtils.formatDateLocal(values.contractEndDate)
      : null;

  return {
    realFirstName: trimToNullable(values.realFirstName),
    realLastName: trimToNullable(values.realLastName),
    privatePhone: trimToNullable(values.privatePhone),
    privateEmail: trimToNullable(values.privateEmail),
    workStartDate: values.workStartDate ? DateUtils.formatDateLocal(values.workStartDate) : null,
    birthDate: values.birthDate ? DateUtils.formatDateLocal(values.birthDate) : null,
    location: trimToNullable(values.location),
    contractType: values.contractType,
    contractEndDate,
    contactPerson: trimToNullable(values.contactPerson),
    languages: values.languages ?? [],
  };
}

const ProfileUserDataModal = ({
  opened,
  onClose,
  onSubmit,
  loading,
  options,
  optionsLoading,
  userData,
  user,
  canEditWorkName = false,
}: ProfileUserDataModalProps) => {
  const { t } = useTranslation();

  const form = useForm<ProfileUserDataFormValues>({
    initialValues: EMPTY_FORM_VALUES,
    validate: {
      workFirstName: (value) =>
        canEditWorkName && value.trim().length === 0 ? t("error.required") : null,
      workLastName: (value) =>
        canEditWorkName && value.trim().length === 0 ? t("error.required") : null,
      privateEmail: (value) =>
        value.trim().length > 0 && !AppUtils.isEmailValid(value)
          ? t("profilePage.editModal.fields.privateEmail.error.email")
          : null,
      contractEndDate: (value, values) =>
        !isPermanentContractType(values.contractType) && !value ? t("error.required") : null,
    },
  });

  const initialFormValues = useMemo(
    () => mapUserDataToFormValues(userData, user),
    [userData, user],
  );

  useEffect(() => {
    if (!opened) {
      form.reset();
      form.clearErrors();
      return;
    }

    form.setValues(initialFormValues);
    form.clearErrors();
    // `form` is intentionally omitted to avoid a render loop from unstable references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFormValues, opened]);

  const languageOptions = useMemo(
    () =>
      (options?.languages ?? []).map((value) => ({
        value,
        label: t(`user.language.options.${value}`, { defaultValue: value }),
      })),
    [options?.languages, t],
  );

  const contractTypeOptions = useMemo(
    () =>
      (options?.contractTypes ?? []).map((value) => ({
        value,
        label: t(`profilePage.contractTypeOptions.${value}`, { defaultValue: value }),
      })),
    [options?.contractTypes, t],
  );

  const shouldShowContractEndDate = !isPermanentContractType(form.values.contractType);

  const handleSubmit = useCallback(async () => {
    const validation = form.validate();

    if (validation.hasErrors) {
      return;
    }

    const values = form.getValues();
    const workName = {
      firstName: values.workFirstName.trim(),
      lastName: values.workLastName.trim(),
    };
    const currentWorkName = getWorkName(user);
    const isWorkNameChanged =
      workName.firstName !== currentWorkName.firstName ||
      workName.lastName !== currentWorkName.lastName;

    await onSubmit(
      buildPayload(values),
      canEditWorkName && isWorkNameChanged ? workName : undefined,
    );
  }, [canEditWorkName, form, onSubmit, user]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("profilePage.editModal.title")}
      size={760}
    >
      <Stack gap="md">
        {canEditWorkName && (
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label={t("profilePage.editModal.fields.workFirstName.label")}
              key={form.key("workFirstName")}
              {...form.getInputProps("workFirstName")}
              disabled={loading}
              required
            />
            <TextInput
              label={t("profilePage.editModal.fields.workLastName.label")}
              key={form.key("workLastName")}
              {...form.getInputProps("workLastName")}
              disabled={loading}
              required
            />
          </SimpleGrid>
        )}

        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput
            label={t("profilePage.editModal.fields.realFirstName.label")}
            key={form.key("realFirstName")}
            {...form.getInputProps("realFirstName")}
            disabled={loading}
          />
          <TextInput
            label={t("profilePage.editModal.fields.realLastName.label")}
            key={form.key("realLastName")}
            {...form.getInputProps("realLastName")}
            disabled={loading}
          />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput
            label={t("profilePage.header.fields.privatePhone")}
            key={form.key("privatePhone")}
            {...form.getInputProps("privatePhone")}
            disabled={loading}
          />
          <TextInput
            label={t("profilePage.header.fields.privateEmail")}
            key={form.key("privateEmail")}
            {...form.getInputProps("privateEmail")}
            disabled={loading}
          />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <DatePicker
            label={t("profilePage.header.fields.startDate")}
            key={form.key("workStartDate")}
            {...form.getInputProps("workStartDate")}
            disabled={loading}
            clearable
          />
          <DatePicker
            label={t("profilePage.header.fields.birthDate")}
            key={form.key("birthDate")}
            {...form.getInputProps("birthDate")}
            disabled={loading}
            clearable
          />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput
            label={t("profilePage.header.fields.location")}
            key={form.key("location")}
            {...form.getInputProps("location")}
            disabled={loading}
          />
          <Select
            label={t("profilePage.header.fields.contractType")}
            data={contractTypeOptions}
            value={form.values.contractType}
            onChange={(value) => {
              form.setFieldValue("contractType", value);

              if (isPermanentContractType(value)) {
                form.setFieldValue("contractEndDate", null);
              }
            }}
            placeholder={t("select.something")}
            disabled={loading || optionsLoading}
            nothingFoundMessage={t("results.no")}
            clearable
          />
        </SimpleGrid>

        {shouldShowContractEndDate && (
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <DatePicker
              label={t("profilePage.header.fields.contractEndDate")}
              key={form.key("contractEndDate")}
              {...form.getInputProps("contractEndDate")}
              disabled={loading}
              clearable
              required
            />
          </SimpleGrid>
        )}

        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <MultiSelect
            label={t("profilePage.header.fields.languages")}
            data={languageOptions}
            value={form.values.languages}
            onChange={(value) => form.setFieldValue("languages", value)}
            placeholder={t("select.something")}
            disabled={loading || optionsLoading}
            nothingFoundMessage={t("results.no")}
            searchable={false}
            clearable
          />
        </SimpleGrid>

        <Textarea
          label={t("profilePage.header.fields.contactPerson")}
          value={form.values.contactPerson}
          onChange={(event) => form.setFieldValue("contactPerson", event.currentTarget.value)}
          disabled={loading}
          autosize
          minRows={2}
          maxRows={4}
        />

        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <Button variant="outline" color="gray" onClick={onClose} disabled={loading}>
            {t("action.cancel")}
          </Button>
          <Button onClick={() => void handleSubmit()} loading={loading}>
            {t("action.save")}
          </Button>
        </SimpleGrid>
      </Stack>
    </Modal>
  );
};

export default ProfileUserDataModal;
